/* Content → Paid content → Sales: sales outside the sales ladder. "Send on
   request" is the content-request policy (the fan asks to buy); "Proactive
   send" is the PPV-offer policy (the AI opens a locked offer). Each has its
   own Advanced disclosure. Edits land in the drafts the page autosaves. */

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ContentPolicyBody, PpvOfferPolicyBody } from '../../../../api/client';
import { AdvancedToggle, Card, Dropdown, InfoLine, NumInput, Row, Section, Seg, Select, Stack, TextArea, Toggle, Btn } from '../kit';
import { font } from '../style';
import { HooksTable } from '../HooksTable';
import { C } from '../tokens';

type P<T> = (p: Partial<T>) => void;

const Grid = ({ min, children }: { min: number; children: ReactNode }) => (
  <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden', display: 'grid', gridTemplateColumns: `repeat(auto-fit,minmax(${min}px,1fr))` }}>{children}</div>
);
const Cell = ({ children, span }: { children: ReactNode; span?: boolean }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 14px', borderTop: `1px solid ${C.divider}`, marginTop: -1, gridColumn: span ? '1/-1' : undefined }}>{children}</div>
);
const Lbl = ({ n, h }: { n: string; h?: string }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
    <span style={{ font: font(500, 13), color: C.text, whiteSpace: h ? undefined : 'nowrap' }}>{n}</span>
    {h && <span style={{ font: font(400, 11.5), color: C.sub }}>{h}</span>}
  </div>
);

export function SalesTab({ policy, setPolicy, ppv, setPpv, ppvReady }: { policy: ContentPolicyBody; setPolicy: P<ContentPolicyBody>; ppv: PpvOfferPolicyBody; setPpv: P<PpvOfferPolicyBody>; ppvReady: boolean }) {
  const { t } = useTranslation('assistantCompact');
  const { t: tt } = useTranslation('tips');
  const [rqAdv, setRqAdv] = useState(false);
  const [ppvAdv, setPpvAdv] = useState(false);
  const [hookTab, setHookTab] = useState<'first' | 'up'>('first');
  const automate = policy.automate_mode === 'automate';
  const hooks = hookTab === 'first' ? ppv.sell_hook_styles : ppv.upsell_hook_styles;
  const RDY = (['eager', 'balanced', 'cautious'] as const).map((id) => ({ id, label: t(`sales.rdy.${id}`), sub: t(`sales.rdy.${id}Sub`) }));

  return (
    <Stack>
      <InfoLine>{t('sales.info')}</InfoLine>

      <Section title={t('sales.request')} hint={t('sales.requestHint')}>
        <Card>
          <Row label={t('sales.whenAsks')} help={automate ? t('sales.askAutomate') : t('sales.askHandoff')}>
            <Seg value={policy.automate_mode} onChange={(v) => setPolicy({ automate_mode: v })} options={[{ id: 'automate', label: t('sales.automate') }, { id: 'handoff', label: t('sales.handoff') }]} />
          </Row>
          <Row label={t('sales.noMatch')} help={policy.no_content_handoff ? t('sales.noMatchHandoff') : t('sales.noMatchAlt')}>
            <Seg value={policy.no_content_handoff ? 'h' : 'a'} onChange={(v) => setPolicy({ no_content_handoff: v === 'h' })} options={[{ id: 'h', label: t('sales.handoff') }, { id: 'a', label: t('sales.offerAlt') }]} />
          </Row>
        </Card>
        <AdvancedToggle open={rqAdv} onToggle={() => setRqAdv((a) => !a)} hint={t('sales.rqAdvHint')} />
        {rqAdv && (
          <Card>
            <Row label={t('sales.drops')} help={t('sales.dropsHelp')}><NumInput value={policy.max_negotiation_attempts} onChange={(v) => v !== null && setPolicy({ max_negotiation_attempts: v })} min={0} max={5} int width={64} /></Row>
            <Row label={t('sales.exhausted')} help={t('sales.exhaustedHelp')}>
              <Seg value={policy.on_max_negotiations} onChange={(v) => setPolicy({ on_max_negotiations: v })} options={[
                { id: 'ease_to_communication', label: t('sales.ease') }, { id: 'notify', label: t('sales.notify') }, { id: 'adjust_price_to_floor', label: t('sales.dropFloor') },
              ]} />
            </Row>
            <Row stack label={t('sales.strategy')} help={t('optional')}>
              <TextArea value={policy.strategy_prompt ?? ''} onChange={(v) => setPolicy({ strategy_prompt: v || null })} placeholder={t('sales.strategyPh')} />
            </Row>
          </Card>
        )}
      </Section>

      <Section title={t('sales.proactive')} hint={t('sales.proactiveHint')}>
        {!ppvReady ? <span style={{ font: font(400, 12), color: C.muted }}>{t('loadFailed')}</span> : (
          <>
            <Card clip={false}>
              <Row label={t('sales.ppvOn')} help={t('sales.ppvOnHelp')}><Toggle on={ppv.is_active} onChange={(v) => setPpv({ is_active: v })} /></Row>
              <Row label={tt('fields.minSessionTurns')} help={t('sales.warmHelp')}>
                <NumInput value={ppv.min_session_turns} onChange={(v) => v !== null && setPpv({ min_session_turns: v })} min={0} max={999} int /><span>{t('turns')}</span>
              </Row>
              <Row label={t('sales.readiness')} help={t('sales.readinessHelp', { note: RDY.find((r) => r.id === ppv.propose_aggression)?.sub ?? '' })}>
                <Dropdown value={ppv.propose_aggression} options={RDY} onChange={(v) => setPpv({ propose_aggression: v })} />
              </Row>
            </Card>
            <AdvancedToggle open={ppvAdv} onToggle={() => setPpvAdv((a) => !a)} hint={t('sales.ppvAdvHint')} />
            {ppvAdv && (
              <Stack>
                <Section title={t('sales.flow')}>
                  <Card>
                    <Row label={tt('fields.teaseBeforeSend')} help={t('sales.teaseHelp')}><Toggle on={ppv.tease_before_send} onChange={(v) => setPpv({ tease_before_send: v })} /></Row>
                    <Row label={t('sales.upsell')} help={t('sales.upsellHelp')}>
                      {ppv.allow_upsell && <><span>{t('sales.maxChain')}</span><NumInput value={ppv.max_upsell_chain} onChange={(v) => setPpv({ max_upsell_chain: v })} min={0} max={99} int allowEmpty placeholder="∞" width={52} /></>}
                      <Toggle on={ppv.allow_upsell} onChange={(v) => setPpv({ allow_upsell: v })} />
                    </Row>
                    <Row label={tt('fields.handoffOnAgreement')} help={t('sales.handHelp')}><Toggle on={ppv.handoff_on_agreement} onChange={(v) => setPpv({ handoff_on_agreement: v })} /></Row>
                  </Card>
                </Section>
                <Section title={t('sales.pacing')} hint={t('sales.pacingHint')}>
                  <Grid min={260}>
                    {([
                      ['max_offers_per_session', 'maxPerSession', 99, false],
                      ['min_turns_between_offers', 'between', 999, false],
                      ['cooldown_turns_after_decline', 'cooldown', 999, false],
                      ['lifetime_cap', 'lifetime', 999, true],
                    ] as const).map(([k, key, max, nul]) => (
                      <Cell key={k}>
                        <Lbl n={t(`sales.pace.${key}`)} h={t(`sales.pace.${key}Help`)} />
                        <NumInput value={ppv[k]} onChange={(v) => (nul || v !== null) && setPpv({ [k]: v } as Partial<PpvOfferPolicyBody>)} min={0} max={max} int allowEmpty={nul} placeholder={nul ? '∞' : undefined} />
                      </Cell>
                    ))}
                  </Grid>
                </Section>
                <Section title={t('sales.hooks')} hint={t('sales.hooksHint')} right={<Seg value={hookTab} onChange={setHookTab} options={[{ id: 'first', label: t('sales.firstN', { count: ppv.sell_hook_styles.length }) }, { id: 'up', label: t('sales.upN', { count: ppv.upsell_hook_styles.length }) }]} />}>
                  <HooksTable
                    hooks={hooks}
                    onChange={(h) => setPpv(hookTab === 'first' ? { sell_hook_styles: h } : { upsell_hook_styles: h })}
                    promptPlaceholder={t('sales.hookPh')}
                    footExtra={hookTab === 'up' ? <Btn kind="muted" height={26} onClick={() => setPpv({ upsell_hook_styles: ppv.sell_hook_styles.map((h) => ({ ...h })) })}>{t('sales.copyFirst')}</Btn> : undefined}
                    footHint={hookTab === 'up' ? t('sales.upHint') : t('sales.firstHint')}
                  />
                </Section>
                <Section title={t('sales.nego')} hint={t('sales.negoHint')}>
                  <Grid min={200}>
                    {([
                      ['negotiation_grace_turns', 'grace'],
                      ['max_negotiation_attempts', 'attempts'],
                      ['max_negotiation_turns', 'turns'],
                    ] as const).map(([k, key]) => (
                      <Cell key={k}>
                        <Lbl n={t(`sales.negoF.${key}`)} />
                        <NumInput value={ppv[k]} onChange={(v) => v !== null && setPpv({ [k]: v } as Partial<PpvOfferPolicyBody>)} min={0} max={20} int />
                      </Cell>
                    ))}
                    <Cell span>
                      <Lbl n={t('sales.onMax')} />
                      <Select value={ppv.on_max_negotiations} onChange={(v) => setPpv({ on_max_negotiations: v })} width={260} options={[
                        { id: 'ease', label: tt('onMax.ppvEase') }, { id: 'notify', label: tt('onMax.ppvNotify') }, { id: 'hold', label: tt('onMax.ppvHold') },
                      ]} />
                    </Cell>
                  </Grid>
                </Section>
                <Section title={t('sales.prompts')} hint={t('sales.promptsHint')}>
                  <Grid min={300}>
                    {([
                      ['propose_conditions', 'when'],
                      ['theme_guidance', 'theme'],
                      ['offer_strategy', 'strategy'],
                    ] as const).map(([k, key]) => (
                      <div key={k} style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '10px 14px' }}>
                        <span style={{ font: font(500, 13), color: C.text }}>{t(`sales.p.${key}`)}</span>
                        <TextArea value={ppv[k] ?? ''} onChange={(v) => setPpv({ [k]: v } as Partial<PpvOfferPolicyBody>)} placeholder={t(`sales.p.${key}Ph`)} />
                      </div>
                    ))}
                  </Grid>
                </Section>
              </Stack>
            )}
          </>
        )}
      </Section>
    </Stack>
  );
}
